import cx from 'classnames';

import CreateAction from '~/components/Actions/CreateAction';
import { useWebThemeContext } from '~/components/Layout/Context/useWebThemeContext';
import MarkDownRender from '~/components/Markdown/MarkDownRender';
import Signup from '~/components/Signup';
import type { SignupStyles } from '~/components/Signup/types';
import Typography from '~/components/Typography';
import type { LandingPage } from '~/interfaces/landing_page';
import type { Publication } from '~/interfaces/publication';
import { BG_OPACITY_OPTIONS } from '~/utils/designOptions';
import normalizeFontType from '~/utils/normalizeFontType';

import Logo from './Logo';
import PostsCTA from './PostsCTA';

interface Props {
  hasPosts: boolean;
  publication: Publication;
  landingPage: LandingPage;
  signupWidgetConfigs?: SignupStyles;
}

const MainSection = ({ hasPosts, publication, landingPage, signupWidgetConfigs }: Props) => {
  const { isEnabled: hasWebThemesEnabled } = useWebThemeContext();
  const { main_section, main_image_url, main_bg_image_url } = landingPage;
  const opacityClass = BG_OPACITY_OPTIONS[main_section.bg_gloss_opacity || '0'];
  const logoEnabled = main_section?.logo_enabled;
  const hideLogo = typeof logoEnabled === 'boolean' && logoEnabled === false;

  return (
    <div
      style={{
        backgroundImage: main_bg_image_url && `url(${main_bg_image_url})`,
        backgroundColor: main_section.bg_color,
      }}
      className={cx('relative', main_bg_image_url && 'bg-cover bg-center bg-no-repeat')}
    >
      {main_bg_image_url && (
        <div
          className={cx('absolute left-0 top-0 h-full w-full', opacityClass)}
          style={{
            backgroundColor: main_section.bg_color,
          }}
        />
      )}
      <div
        className={cx({
          'relative z-10 flex items-center px-4 sm:px-8': true,
          'min-h-screen pb-8 pt-16 sm:pb-0 sm:pt-0': !main_section.disable_full_height,
          'pb-16 pt-28': main_section.disable_full_height,
        })}
      >
        <div className={cx('mx-auto -mt-10 flex w-full max-w-5xl space-x-0 sm:space-x-8')}>
          <div className="w-full">
            <div
              className={cx({
                'flex flex-col items-center sm:items-start': true,
                'sm:items-center': !main_image_url,
              })}
            >
              {!hideLogo && (
                <div className="mb-8">
                  <Logo publication={publication} className="h-24 w-24" />
                </div>
              )}

              <div className="mb-6 w-full">
                <div className="mb-2 flex w-full flex-col items-center">
                  {main_section.overline && (
                    <div
                      className={cx({
                        'flex w-full justify-center sm:justify-start': true,
                        'justify-center': !main_image_url,
                      })}
                    >
                      <Typography
                        as="span"
                        size={main_section.overline_font_size}
                        weight="bold"
                        fontFamily={main_section.overline_font_family}
                        fontFamilyv2={hasWebThemesEnabled ? main_section.overline_font_family : undefined}
                        color={main_section.overline_color}
                        className={cx('w-full', main_image_url ? 'text-center sm:text-left' : 'text-center')}
                      >
                        <MarkDownRender>{main_section.overline}</MarkDownRender>
                      </Typography>
                    </div>
                  )}

                  {main_section.headline && (
                    <Typography
                      as="h1"
                      size={main_section.headline_font_size}
                      weight="bold"
                      fontFamily={main_section.headline_font_family}
                      fontFamilyv2={hasWebThemesEnabled ? main_section.headline_font_family : undefined}
                      color={main_section.headline_color}
                      className={cx({
                        'w-full break-words text-center sm:break-normal sm:text-left': true,
                        'max-w-3xl sm:text-center': !main_image_url,
                      })}
                    >
                      <MarkDownRender>{main_section.headline}</MarkDownRender>
                    </Typography>
                  )}
                </div>

                {main_section.description && (
                  <Typography
                    as="span"
                    size={main_section.description_font_size}
                    weight="regular"
                    fontFamily={main_section.description_font_family}
                    fontFamilyv2={hasWebThemesEnabled ? main_section.description_font_family : undefined}
                    color={main_section.description_color}
                    className={cx({
                      'w-full': true,
                      'text-center sm:text-left': main_image_url,
                      'mx-auto max-w-3xl text-center': !main_image_url,
                    })}
                  >
                    <MarkDownRender>{main_section.description}</MarkDownRender>
                  </Typography>
                )}
              </div>

              <div
                className={cx({
                  'mb-10 flex w-full flex-col': true,
                  'items-center': !main_image_url,
                })}
              >
                <div className="w-full max-w-lg">
                  {hasWebThemesEnabled && signupWidgetConfigs ? (
                    <Signup signupStyles={signupWidgetConfigs} />
                  ) : (
                    <CreateAction
                      publication={publication}
                      autoFocus={true}
                      backgroundColor={main_section.signup_bg_color}
                      textColor={main_section.signup_text_color}
                      placeholderText={main_section.signup_placeholder_text}
                      cta={main_section.signup_cta_text}
                      fontFamily={normalizeFontType(main_section.signup_cta_font_family)}
                    />
                  )}
                </div>
                {main_section.underline && (
                  <Typography
                    as="span"
                    size={main_section.underline_font_size}
                    weight="regular"
                    fontFamily={main_section.underline_font_family}
                    fontFamilyv2={hasWebThemesEnabled ? main_section.underline_font_family : undefined}
                    color={main_section.underline_color}
                    className={cx({
                      'w-full pt-4 text-center sm:text-left': true,
                      'sm:text-center': !main_image_url,
                    })}
                  >
                    <MarkDownRender>{main_section.underline}</MarkDownRender>
                  </Typography>
                )}
              </div>

              {hasPosts && main_section.read_more_cta_enabled && (
                <div
                  style={{
                    color: main_section.read_more_cta_color,
                  }}
                  className={cx({
                    'w-full text-center sm:text-left': true,
                    'sm:text-center': !main_image_url,
                  })}
                >
                  <PostsCTA
                    ctaText={main_section.read_more_cta}
                    fontSize={main_section.read_more_cta_font_size}
                    fontFamily={main_section.read_more_cta_font_family}
                  />
                </div>
              )}
            </div>
          </div>

          {main_image_url && (
            <div className="flex items-center">
              <div className={'hidden w-64 sm:block'}>
                <img src={main_image_url || ''} alt="Main depiction of the publication on the landing page" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MainSection;
